import { useMemo, useCallback } from "react";
import { TransportDistribution } from "@/types/transport-distribution/transport-distribution.types";
import { FilterOption } from "@/components/ui/table-header/types";
import { normalizeText } from "@/utils/textFormatter";

interface UseTransportDistributionFiltersProps {
  transportDistributions: TransportDistribution[];
  searchTerm: string;
  showArchived: boolean;
  vehicleTypeFilters: string[];
}

export const useTransportDistributionFilters = ({
  transportDistributions,
  searchTerm,
  showArchived,
  vehicleTypeFilters,
}: UseTransportDistributionFiltersProps) => {
  const filteredDistributions = useMemo(() => {
    if (!transportDistributions) return [];

    return transportDistributions.filter((distribution) => {
      // Filter by archive status
      if (showArchived) {
        if (!distribution.isArchived) return false;
      } else {
        if (distribution.isArchived) return false;
      }

      // Filter by vehicle type
      if (vehicleTypeFilters.length > 0 && !vehicleTypeFilters.includes(distribution.vehicleType)) {
        return false;
      }

      // Filter by search term
      if (searchTerm) {
        const term = normalizeText(searchTerm);
        const normalizedVehicleType = normalizeText(distribution.vehicleType);

        return normalizedVehicleType.includes(term);
      }

      return true;
    });
  }, [transportDistributions, searchTerm, showArchived, vehicleTypeFilters]);

  const getUniqueOptions = useCallback(
    (field: keyof TransportDistribution): FilterOption[] => {
      if (!transportDistributions) return [];

      const filteredByOtherFilters = transportDistributions.filter((distribution) => {
        // Filter by archive status
        if (showArchived) {
          if (!distribution.isArchived) return false;
        } else {
          if (distribution.isArchived) return false;
        }

        // Filter by search term
        if (searchTerm) {
          const term = normalizeText(searchTerm);
          const normalizedVehicleType = normalizeText(distribution.vehicleType);

          if (!normalizedVehicleType.includes(term)) {
            return false;
          }
        }

        // Filter by vehicle type (if not the current field)
        if (field !== "vehicleType" && vehicleTypeFilters.length > 0 && !vehicleTypeFilters.includes(distribution.vehicleType)) {
          return false;
        }

        return true;
      });

      const uniqueValues = new Set<string>();

      filteredByOtherFilters.forEach((distribution) => {
        const value = distribution[field];
        if (value && typeof value === "string") {
          uniqueValues.add(value);
        }
      });

      return Array.from(uniqueValues)
        .sort()
        .map((value) => ({
          label: value,
          value,
        }));
    },
    [transportDistributions, searchTerm, showArchived, vehicleTypeFilters]
  );

  return {
    filteredDistributions,
    getUniqueOptions,
  };
}; 